//@flow

import find from 'lodash/find';
import flatten from 'lodash/flatten';
// $FlowFixMe - out react-redux def files doesn't know about the batch function yet
import {batch} from 'react-redux';

import * as request from '../utils/request';
import * as urls from '../urls';
import type {RolesFetchParamsType, RoleType} from './types';
import type {ResponseType} from 'app/utils/redux_v2';
import {gt} from 'app/utils/i18n';
import {DEFAULT_PAGE_SIZE, UNLIMITED_PAGE_SIZE} from 'app/utils/constants';
import type {BasicUserType} from 'app/user/types';
import type {TeamType} from 'app/teams/types';
import {fetchUserList} from 'app/user/actions';
import {del} from '../utils/request';
import {showNotification} from 'app/toasts/slice';
import {createErrorHandler} from '../utils/request';
import {showForbiddenMessageHandler} from '../utils/request';

export type RoleActionType = ReceiveRoleListActionType;

export const convertLocationParamsToFetchParams = (
  query: RolesFetchParamsType
): RolesFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
  team_id: query.team_id !== '-1' ? query.team_id : undefined,
  team_ids: query.team_ids != null ? query.team_ids : undefined,
  search: query.search != null ? query.search : undefined,
});

export const addRole = (team: TeamType, name: string) => () =>
  request.postJson(urls.TEAM_ROLES_API_URL, {team: team.url, name});

export const addUserToRole = (user: BasicUserType, role: RoleType) => () =>
  request.postJson(urls.TEAM_ROLE_MEMBERSHIPS_API_URL, {
    user: user.url,
    role: role.url,
  });

export const removeUserFromRole =
  (role: RoleType, user_url: string) => (dispatch: any) => {
    const membership = find(role.role_memberships, {user_url});

    if (membership != null) {
      return request.del(membership.url);
    }
    dispatch(showNotification(gt('Member not found in role'), 'error'));
    return Promise.reject(Error('Member not found in role'));
  };

const requestRoles = (params?: any): Promise<ResponseType<RoleType>> =>
  request.get(urls.TEAM_ROLES_API_URL, params);

export const fetchRolesAndUsers =
  (fetchParams: RolesFetchParamsType) =>
  /*
    this function fetches both roles and roles-users.

    But to avoid updating the frontend while the users weren't fetched yet,
    the receiveRoleList func is called only after the users were saved to the
    redux store in the dispatch(fetchUserList()) call.
     */
  (dispatch: any) =>
    batch(() =>
      requestRoles(fetchParams).then((rolesResponse) => {
        const usersToFetch = flatten(rolesResponse.results.map((r) => r.users));

        return Promise.resolve(
          usersToFetch.length > 0
            ? dispatch(
                fetchUserList({
                  id: usersToFetch.join(','),
                  page_size: UNLIMITED_PAGE_SIZE,
                })
              )
            : null
        ).then(() => dispatch(receiveRoleList(fetchParams, rolesResponse)));
      })
    );

export const RECEIVE_ROLE_LIST = 'RECEIVE_ROLE_LIST';

type ReceiveRoleListActionType = {
  type: 'RECEIVE_ROLE_LIST',
  query: RolesFetchParamsType,
  response: ResponseType<RoleType>,
};

export const receiveRoleList = (
  query: RolesFetchParamsType,
  response: ResponseType<RoleType>
): ReceiveRoleListActionType => ({
  type: RECEIVE_ROLE_LIST,
  query,
  response,
});

export const deleteRole = (role: RoleType) => (dispatch: (any) => any) =>
  del(role.url).then(
    () => dispatch(showNotification(gt('Role deleted'))),
    createErrorHandler(
      gt('Unable to delete role'),
      dispatch,
      showForbiddenMessageHandler(true)
    )
  );

export const updateRole =
  (roleURL: string, updates: $Shape<RoleType>) => (dispatch: FuncDispatch) =>
    request
      .patchJson(roleURL, updates)
      .catch(createErrorHandler(gt('Unable to update role.'), dispatch));
